/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { useDocumentTitle } from 'shared-ui/utils/seo-utils';
import { useOrderDetails } from '../../shared';
import { OrderDetailsPage } from '../../components/OrderDetailsPage/OrderDetailsPage';

export function OrderDetailsPageContainer() {
    const validDays = useSelector(
        (state) => state?.timedEntry?.orderDetails?.reservation?.valid_days
    );
    const tourUsingSitePassIntegration = useSelector(
        (state) =>
            state?.timedEntry?.tours?.tourList?.find(
                (tourItem) =>
                    tourItem?.tour_id === state?.timedEntry?.orderDetails?.tour_id
            )?.site_pass_integration?.required
    );

    const { reservationId } = useParams();
    const { pathname } = useLocation();
    const showSitePasses = useMemo(() => {
        return !pathname?.includes('/modify') || tourUsingSitePassIntegration;
    }, [pathname, tourUsingSitePassIntegration]);
    const {
        facilityId,
        imageUrl,
        acceptsPasses,
        isLoading,
        orderDetailsData,
        isReservationConfirmed,
        hasPendingModification,
        handleModifyClick,
        handleRemoveClick,
        isNeedToKnowChecked,
        isSitePassInfoChecked,
        proceedToCartButtonText,
        isSpinning,
        addValidationRef,
        validationErrors,
        apiError,
        handleToggleNeedToKnowCheckbox,
        handleToggleSitePassInfoCheckbox,
        handleProceedToCart,
        handleContinueShopping,
        isChallengeModalOpen,
        recaptchaCheckboxProps,
        isV2Invalid,
        handleChallengeContinue,
        tour,
        reservation,
        cartReservations,
        shouldShowSitePasses,
        availableSitePassForSelectedDate,
        userMustBuyAPassOnPurchase,
        sitePassReservationsExist,
        newSitePassIntegrationEnabled,
        sitePassNotRequiredForDate,
        isFacilitySitePassMapped,
        showDigitalPassDiscounts,
    } = useOrderDetails(reservationId, true, showSitePasses);

    useDocumentTitle(`Order Details - ${process.env.SITE_NAME}`);

    return (
        <OrderDetailsPage
            facilityId={facilityId}
            imageUrl={imageUrl}
            acceptsPasses={acceptsPasses}
            isLoading={isLoading}
            orderDetails={orderDetailsData}
            isExistingReservation={isReservationConfirmed}
            hasPendingModification={hasPendingModification}
            onModifyClick={handleModifyClick}
            onRemoveClick={handleRemoveClick}
            isNeedToKnowChecked={isNeedToKnowChecked}
            isSitePassInfoChecked={isSitePassInfoChecked}
            proceedToCartButtonText={proceedToCartButtonText}
            isSpinning={isSpinning}
            addValidationRef={addValidationRef}
            validationErrors={validationErrors}
            apiError={apiError}
            onNeedToKnowChange={handleToggleNeedToKnowCheckbox}
            onSitePassInfoChange={handleToggleSitePassInfoCheckbox}
            onProceedToCartClick={handleProceedToCart}
            onContinueShopping={handleContinueShopping}
            isChallengeModalOpen={isChallengeModalOpen}
            recaptchaCheckboxProps={recaptchaCheckboxProps}
            isV2Invalid={isV2Invalid}
            onChallengeContinue={handleChallengeContinue}
            tour={tour}
            reservation={reservation}
            cartReservations={cartReservations}
            validDays={validDays}
            showSitePasses={shouldShowSitePasses}
            availableSitePass={availableSitePassForSelectedDate}
            userMustBuyAPassOnPurchase={userMustBuyAPassOnPurchase}
            sitePassReservationsExist={sitePassReservationsExist}
            newSitePassIntegrationEnabled={newSitePassIntegrationEnabled}
            sitePassNotRequiredForDate={sitePassNotRequiredForDate}
            isFacilitySitePassMapped={isFacilitySitePassMapped}
            showDigitalPassDiscounts={showDigitalPassDiscounts}
            showBuyNow
            reservationId={reservationId}
        />
    );
}
